'use client'

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/navigation'

interface HostingProgressModalProps {
  isOpen: boolean
  itemId: string
  packageName: string
  onClose: () => void
}

const STEPS = [
  { label: 'Authenticating point balance & escrow...', duration: 600 },
  { label: 'Allocating dedicated VPS resource container...', duration: 900 },
  { label: 'Provisioning Hestia CP user profile & ports...', duration: 1200 },
  { label: 'Initializing SSL certificates & firewall rules...', duration: 800 },
  { label: 'Finalizing secure server handshake...', duration: 500 }
]

export default function HostingProgressModal({ isOpen, itemId, packageName, onClose }: HostingProgressModalProps) {
  const [mounted, setMounted] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [stepsDone, setStepsDone] = useState(false)
  const [serverData, setServerData] = useState<any>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const router = useRouter()

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0)
      setStepsDone(false)
      setServerData(null)
      setErrorMsg(null)
      setCopied(false)
      return
    }

    let isCancelled = false

    // 1. Trigger backend purchase & provisioning API
    const executePurchase = async () => {
      try {
        const res = await fetch('/api/store/buy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ itemId })
        })
        const data = await res.json()
        if (isCancelled) return

        if (data.success) {
          const s = data.server || data.hostingDetails || data
          setServerData({
            serverName: s.serverName || s.name || `DevYield Node (${packageName})`,
            ipAddress: s.ipAddress || s.ip || 'panel.devyield.co.uk',
            panelUrl: s.panelUrl || 'https://panel.devyield.co.uk:8083',
            sshUser: s.sshUser || s.username || s.user || 'N/A',
            sshPassword: s.sshPassword || s.password || s.pass || 'N/A'
          })
        } else {
          setErrorMsg(data.error || 'Provisioning failed')
        }
      } catch (err) {
        if (!isCancelled) setErrorMsg('Network error during deployment.')
      }
    }

    executePurchase()

    // 2. Run visual animation steps
    let timeout: NodeJS.Timeout
    const runSteps = (stepIdx: number) => {
      if (isCancelled) return
      if (stepIdx < STEPS.length) {
        timeout = setTimeout(() => {
          setCurrentStep(stepIdx + 1)
          runSteps(stepIdx + 1)
        }, STEPS[stepIdx].duration)
      } else {
        setStepsDone(true)
      }
    }

    runSteps(0)

    return () => {
      isCancelled = true
      clearTimeout(timeout)
    }
  }, [isOpen, itemId, packageName])

  if (!isOpen || !mounted) return null

  const isFullyFinished = stepsDone && serverData !== null
  const progressPercentage = Math.round((Math.min(currentStep, STEPS.length) / STEPS.length) * 100)

  const handleCopyAll = () => {
    if (!serverData) return
    const text = `Node Name: ${serverData.serverName}\nIP Address: ${serverData.ipAddress}\nPanel URL: ${serverData.panelUrl}\nSSH Username: ${serverData.sshUser}\nSSH Password: ${serverData.sshPassword}`
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 z-[99999] font-mono">
      <div className="bg-zinc-950 border border-emerald-500/30 rounded-3xl p-8 max-w-xl w-full shadow-2xl space-y-6 relative overflow-hidden text-zinc-100">
        
        {/* Top Glow Accent */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-amber-500"></div>

        {/* ERROR VIEW */}
        {errorMsg ? (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-400 text-[10px] font-bold uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-ping"></span>
                INFRA PROVISIONING ENGINE
              </div>
              <button onClick={onClose} className="text-xs text-zinc-500 hover:text-white cursor-pointer font-sans">Exit</button>
            </div>

            <h3 className="text-2xl font-black text-white font-sans">Deployment Aborted</h3>

            <div className="bg-rose-950/20 border border-rose-500/30 rounded-2xl p-4 text-xs text-rose-300 space-y-1">
              <div className="font-bold uppercase tracking-wider text-rose-400">Provisioning Fault Detected</div>
              <div>{errorMsg}</div>
            </div>

            <button 
              onClick={onClose}
              className="w-full py-3 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-800 font-bold rounded-2xl text-xs uppercase tracking-wider transition-all cursor-pointer"
            >
              ← Return to Store
            </button>
          </div>
        ) : !isFullyFinished ? (
          /* LOADING / PROGRESS VIEW */
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span>
                INFRA PROVISIONING ENGINE // {packageName}
              </div>
              <button onClick={onClose} className="text-xs text-zinc-500 hover:text-white cursor-pointer font-sans">Exit</button>
            </div>

            <div className="space-y-1">
              <h3 className="text-2xl font-black text-white font-sans">
                {stepsDone ? 'Waiting for server response...' : 'Provisioning Node...'}
              </h3>
              <p className="text-xs text-zinc-400">Allocating dedicated container resources and generating secure credentials.</p>
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-zinc-400">
                <span>Progress</span>
                <span className="text-emerald-400 font-bold">
                  {stepsDone && !serverData ? '99% ' : `${progressPercentage}%`}
                </span>
              </div>
              <div className="w-full bg-zinc-900 h-2.5 rounded-full overflow-hidden border border-zinc-800">
                <div 
                  className="bg-gradient-to-r from-amber-500 to-emerald-400 h-full transition-all duration-300 rounded-full"
                  style={{ width: stepsDone && !serverData ? '99%' : `${progressPercentage}%` }}
                ></div>
              </div>
            </div>

            {/* Step Log Output */}
            <div className="bg-zinc-900/60 p-4 rounded-2xl border border-zinc-800 space-y-2 max-h-40 overflow-y-auto text-xs">
              {STEPS.slice(0, currentStep).map((step, idx) => (
                <div key={idx} className="flex items-center gap-2 text-emerald-400">
                  <span>✓</span>
                  <span className="text-zinc-300">{step.label}</span>
                </div>
              ))}
              {currentStep < STEPS.length && (
                <div className="flex items-center gap-2 text-amber-400 animate-pulse">
                  <span>⚡</span>
                  <span className="text-amber-300">{STEPS[currentStep]?.label}</span>
                </div>
              )}
              {stepsDone && !serverData && (
                <div className="flex items-center gap-2 text-amber-400 animate-pulse">
                  <span>⏳</span>
                  <span className="text-amber-300">Establishing secure connection with Hestia CP node...</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* SUCCESS CREDENTIALS VIEW */
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                INFRA PROVISIONING ENGINE
              </div>
              <button onClick={onClose} className="text-xs text-zinc-500 hover:text-white cursor-pointer font-sans">Exit</button>
            </div>

            <h3 className="text-2xl font-black text-white font-sans">Server Live &amp; Online</h3>

            {/* Success Banner */}
            <div className="bg-emerald-950/20 border border-emerald-500/30 rounded-2xl p-4 text-xs text-emerald-300 space-y-1">
              <div className="font-bold flex items-center gap-2 text-emerald-400">
                <span>🚀</span> Infrastructure successfully deployed!
              </div>
              <div className="text-zinc-400">Your points have been deducted and credentials securely generated.</div>
            </div>

            {/* Credentials Grid */}
            <div className="space-y-3">
              <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Access Credentials</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                
                <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-3.5 space-y-1">
                  <div className="text-[10px] text-zinc-500 uppercase">Node Name</div>
                  <div className="font-bold text-zinc-200 truncate">{serverData?.serverName}</div>
                </div>

                <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-3.5 space-y-1">
                  <div className="text-[10px] text-zinc-500 uppercase">IP Address</div>
                  <div className="font-bold text-emerald-400 truncate">{serverData?.ipAddress}</div>
                </div>

                <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-3.5 space-y-1">
                  <div className="text-[10px] text-zinc-500 uppercase">SSH Username</div>
                  <div className="font-bold text-amber-400 truncate">{serverData?.sshUser}</div>
                </div>

                <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-3.5 space-y-1">
                  <div className="text-[10px] text-zinc-500 uppercase">SSH Password</div>
                  <div className="font-bold text-amber-400 truncate">{serverData?.sshPassword}</div>
                </div>

              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button 
                onClick={handleCopyAll}
                className="flex-1 py-3 bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border border-zinc-800 font-bold rounded-2xl text-xs uppercase tracking-wider transition-all cursor-pointer shadow-lg"
              >
                {copied ? '✓ Credentials Copied!' : 'Copy Credentials'}
              </button>
              
              <button 
                onClick={() => {
                  onClose()
                  router.push('/dashboard/hosting')
                  router.refresh()
                }}
                className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-2xl text-xs uppercase tracking-wider transition-all shadow-lg shadow-emerald-600/20 cursor-pointer text-center"
              >
                Go to Hosting Dashboard →
              </button>
            </div>

          </div>
        )}

      </div>
    </div>,
    document.body
  )
}